/*
  Stockage local de la feuille de dépense — le socle du mode hors ligne.

  La feuille se déclare en fin de journée, souvent sans réseau (Kinshasa). Le
  principe : la SAISIE ne dépend jamais du réseau. Elle écrit dans un brouillon
  local (localStorage), et la synchronisation avec le serveur se fait à des
  moments définis (ouverture en ligne, soumission, retour du réseau).

  Trois choses sont mises en cache :
   · le RÉFÉRENTIEL (points, moyens, tarifs), avec son empreinte `version` :
     l'écran fonctionne hors ligne à condition de l'avoir chargé une fois ;
   · les COMMANDES du jour, pour rattacher une livraison hors ligne ;
   · le BROUILLON du jour (les lignes saisies), par utilisateur et par date.

  localStorage peut lever (mode privé, quota) : tout est encapsulé, un échec de
  stockage n'interrompt jamais la saisie — il dégrade seulement la persistance.
*/

const PREFIXE = 'fdep';
const CLE_REF = `${PREFIXE}.referentiel`;
const CLE_CMD = `${PREFIXE}.commandes`;
const cleFavoris = (idUser) => `${PREFIXE}.favoris.${idUser || 0}`;
const cleBrouillon = (idUser, date) => `${PREFIXE}.brouillon.${idUser || 0}.${date}`;

function lire(cle) {
  try {
    const brut = window.localStorage.getItem(cle);
    return brut ? JSON.parse(brut) : null;
  } catch (e) {
    return null;
  }
}

function ecrire(cle, valeur) {
  try {
    window.localStorage.setItem(cle, JSON.stringify(valeur));
    return true;
  } catch (e) {
    return false;
  }
}

function effacer(cle) {
  try { window.localStorage.removeItem(cle); } catch (e) { /* sans effet */ }
}

// --- Référentiel ------------------------------------------------------------
// On ne remplace le cache que si l'empreinte a changé : inutile de réécrire des
// centaines de points identiques à chaque ouverture.
export function lireReferentiel() {
  return lire(CLE_REF);
}
export function ecrireReferentiel(ref) {
  if (!ref || !ref.version) return false;
  const actuel = lireReferentiel();
  if (actuel && actuel.version === ref.version) return true;
  return ecrire(CLE_REF, ref);
}

// --- Commandes du jour ------------------------------------------------------
export function lireCommandes() {
  return lire(CLE_CMD) || [];
}
export function ecrireCommandes(commandes) {
  return ecrire(CLE_CMD, Array.isArray(commandes) ? commandes : []);
}

// --- Trajets favoris --------------------------------------------------------
// Le cœur du « 1 tap » : sans eux hors ligne, le livreur perd les boutons de
// trajets habituels au moment même où il en a le plus besoin (déclaration du
// soir, souvent sans réseau). On les met donc en cache par utilisateur.
export function lireFavoris(idUser) {
  return lire(cleFavoris(idUser)) || [];
}
export function ecrireFavoris(idUser, favoris) {
  return ecrire(cleFavoris(idUser), Array.isArray(favoris) ? favoris : []);
}

// --- Brouillon du jour ------------------------------------------------------
export function lireBrouillon(idUser, date) {
  return lire(cleBrouillon(idUser, date));
}
export function ecrireBrouillon(idUser, date, brouillon) {
  return ecrire(cleBrouillon(idUser, date), { ...brouillon, date, updatedAt: new Date().toISOString() });
}
export function effacerBrouillon(idUser, date) {
  effacer(cleBrouillon(idUser, date));
}

// Identifiant local d'une ligne saisie hors ligne. Pas besoin d'un vrai UUID :
// il ne sert qu'à distinguer les lignes du brouillon en mémoire et à donner une
// clé de rendu stable. Le serveur attribuera le vrai id à la synchronisation.
let compteur = 0;
export function nouveauUid() {
  compteur += 1;
  return `loc-${Date.now().toString(36)}-${compteur}`;
}
